import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  FiShoppingBag,
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiInfo,
  FiRefreshCw,
  FiTag,
  FiMapPin,
  FiLayers,
} from "react-icons/fi";
import {
  getPartnerLocalBazaars,
  getPartnerLocalBazaarCategories,
  addPartnerLocalBazaar,
  editPartnerLocalBazaar,
  deletePartnerLocalBazaar,
  getPartnerLocalBazaarDetails,
  uploadMediaFiles,
} from "../../../api/partner/partnerFeatureApis";
import AIDescriptionAssist from "../../common/ai/AIDescriptionAssist";
import { nameOf } from "../../../utils/aiDescription";

export default function PartnerLocalBazaarHub() {
  const authState = useSelector((state) => state.auth);
  const token = authState?.user?.token || authState?.token || authState?.user?.refreshToken;
  const partnerId = authState?.user?.userDetails?.id || authState?.user?.id;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState(null);

  // Add / Edit Modal state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("piece");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Details Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Fetch Products
  const fetchProducts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAlertMsg(null);
    try {
      const data = await getPartnerLocalBazaars(token, {
        partnerId,
        page_number: 1,
        page_size: 50,
        keyword_search: searchQuery,
        category_id: selectedCategoryFilter,
      });
      const items = data?.localBazaars || data?.localBazaarList || data?.bazaars || [];
      setProducts(items);
    } catch (err) {
      console.error("Failed to load local bazaar products:", err);
      setAlertMsg({ type: "danger", text: "Failed to load marketplace items. Please refresh." });
    } finally {
      setLoading(false);
    }
  }, [token, partnerId, searchQuery, selectedCategoryFilter]);

  // Fetch Categories
  const fetchCategories = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getPartnerLocalBazaarCategories(token);
      setCategories(data?.localBazaarCategories || []);
    } catch (err) {
      console.error("Failed to load bazaar categories:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setEditingProductId(null);
    setSelectedCategoryId(categories[0]?.id ? String(categories[0].id) : "");
    setTitle("");
    setDescription("");
    setPrice("");
    setUnit("piece");
    setCity("");
    setStateName("");
    setSelectedFiles([]);
    setExistingImages([]);
    setFormError("");
    setShowAddEditModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (product) => {
    setIsEditMode(true);
    setEditingProductId(product.id);
    setSelectedCategoryId(
      product.local_bazaar_category_id || product.localBazaarCategory?.id
        ? String(product.local_bazaar_category_id || product.localBazaarCategory?.id)
        : ""
    );
    setTitle(product.title || "");
    setDescription(product.description || "");
    setPrice(product.price ? String(product.price).replace(/[^\d]/g, "") : "");
    setUnit(product.unit || "piece");
    setCity(product.city || "");
    setStateName(product.state || "");
    setSelectedFiles([]);
    setExistingImages(product.photos || product.localBazaar_images || []);
    setFormError("");
    setShowAddEditModal(true);
  };

  // Delete Product
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to permanently delete this product?")) {
      return;
    }
    setActionLoadingId(productId);
    try {
      const res = await deletePartnerLocalBazaar(token, productId);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        setAlertMsg({ type: "success", text: res.text || "Product deleted successfully." });
        setProducts((prev) => prev.filter((p) => p.id !== productId));
      } else {
        setAlertMsg({ type: "danger", text: res?.text || "Failed to delete product." });
      }
    } catch (err) {
      setAlertMsg({ type: "danger", text: err?.response?.data?.text || "Failed to delete product." });
    } finally {
      setActionLoadingId(null);
    }
  };

  // Open Details Modal
  const handleOpenDetails = async (productId) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const data = await getPartnerLocalBazaarDetails(token, productId);
      setSelectedProduct(data?.localBazaar || data?.bazaar || null);
    } catch (err) {
      console.error("Failed to load product details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!title.trim() || !selectedCategoryId || !price) {
      setFormError("Please enter product title, category, and price.");
      return;
    }

    if (!isEditMode && selectedFiles.length === 0) {
      setFormError("Please upload at least one image of the product.");
      return;
    }

    setSubmitting(true);
    try {
      let uploadedImages = [];
      if (selectedFiles.length > 0) {
        const uploadRes = await uploadMediaFiles(token, selectedFiles);
        const propImages = uploadRes?.propertyImages || [];
        uploadedImages = propImages.map((img) => ({
          imagePath: img.imagePath,
          image_url: img.image_url,
          url: img.image_url,
          home: false,
          active: true,
          deleted: false,
        }));
      }

      const allPhotos = isEditMode
        ? [...existingImages, ...uploadedImages]
        : uploadedImages;

      const numericPrice = price.replace(/[^\d]/g, "");

      const payload = {
        title: title.trim(),
        description: description.trim(),
        price: numericPrice,
        unit: unit.trim() || "piece",
        local_bazaar_category_id: Number(selectedCategoryId),
        city: city.trim(),
        state: stateName.trim(),
        photos: allPhotos,
      };

      if (isEditMode) {
        payload.id = editingProductId;
        const res = await editPartnerLocalBazaar(token, payload);
        if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
          setAlertMsg({ type: "success", text: "Product updated successfully!" });
          setShowAddEditModal(false);
          fetchProducts();
        } else {
          setFormError(res?.text || "Failed to update product.");
        }
      } else {
        const res = await addPartnerLocalBazaar(token, payload);
        if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
          setAlertMsg({ type: "success", text: "Product published to Local Bazaar successfully!" });
          setShowAddEditModal(false);
          fetchProducts();
        } else {
          setFormError(res?.text || "Failed to publish product.");
        }
      }
    } catch (err) {
      setFormError(err?.response?.data?.text || "An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-dark">
            <FiShoppingBag className="text-primary" /> Local Bazaar Merchant Hub
          </h3>
          <p className="text-muted mb-0 small">
            Manage your store inventory, hardware supplies, home improvement items, and local marketplace products.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-1 btn-sm"
            onClick={fetchProducts}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "spin-icon" : ""} /> Refresh
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2 btn-sm px-3 shadow-sm"
            onClick={handleOpenAdd}
          >
            <FiPlus /> Add Marketplace Product
          </button>
        </div>
      </div>

      {alertMsg && (
        <div
          className={`alert alert-${alertMsg.type} alert-dismissible fade show d-flex align-items-center justify-content-between mb-3`}
          role="alert"
        >
          <span>{alertMsg.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setAlertMsg(null)}
          />
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-4">
              <select
                className="form-select form-select-sm"
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              >
                <option value="">All Bazaar Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-8">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <FiSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search products by title or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearchQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading products...</span>
          </div>
          <p className="text-muted mt-2 small">Loading your marketplace inventory...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
          <div className="card-body">
            <FiShoppingBag size={48} className="text-muted mb-3 opacity-50" />
            <h5 className="fw-semibold text-secondary">No Products Listed Yet</h5>
            <p className="text-muted small mb-3">
              {searchQuery
                ? `No products matching "${searchQuery}".`
                : "You haven't listed any products in Local Bazaar yet. Click 'Add Marketplace Product' to list items."}
            </p>
            <button className="btn btn-primary btn-sm" onClick={handleOpenAdd}>
              Add Product
            </button>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {products.map((item) => {
            const photos = item.photos || item.localBazaar_images || item.bazaar_photos || [];
            const thumbnail = photos[0]?.url || photos[0]?.image_url
              ? ((photos[0]?.url || photos[0]?.image_url).startsWith("http")
                  ? photos[0]?.url || photos[0]?.image_url
                  : `https://images.allhomedesire.com${photos[0]?.url || photos[0]?.image_url}`)
              : null;
            const isBusy = actionLoadingId === item.id;
            const priceVal = item.price ? Number(item.price).toLocaleString("en-IN") : "0";

            return (
              <div key={item.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <div className="card h-100 border-0 shadow-sm rounded-3 overflow-hidden d-flex flex-column justify-content-between">
                  <div>
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt="Product"
                        className="w-100 object-fit-cover"
                        style={{ height: "160px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="w-100 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center text-muted"
                        style={{ height: "160px" }}
                      >
                        <FiShoppingBag size={36} />
                      </div>
                    )}

                    <div className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="badge bg-light text-secondary border small">
                          {item.localBazaarCategory?.name || "Marketplace"}
                        </span>
                        {item.city && (
                          <span className="text-muted small d-flex align-items-center gap-1">
                            <FiMapPin size={11} className="text-danger" /> {item.city}
                          </span>
                        )}
                      </div>

                      <h6 className="fw-bold text-dark text-truncate mb-1" title={item.title}>
                        {item.title}
                      </h6>

                      <p
                        className="text-muted small mb-2 line-clamp-2"
                        style={{ minHeight: "36px" }}
                      >
                        {item.description || "Verified merchant item for sale."}
                      </p>

                      <div className="fw-bold text-primary fs-5 mb-0">
                        ₹{priceVal}{" "}
                        <span className="text-muted small fw-normal fs-6">
                          /{item.unit || "piece"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 pt-0 border-top mt-2">
                    <div className="d-flex gap-2 pt-2">
                      <button
                        className="btn btn-outline-secondary btn-sm flex-grow-1"
                        onClick={() => handleOpenDetails(item.id)}
                      >
                        <FiInfo size={13} /> View
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm flex-grow-1"
                        onClick={() => handleOpenEdit(item)}
                      >
                        <FiEdit size={13} /> Edit
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm px-3"
                        onClick={() => handleDeleteProduct(item.id)}
                        disabled={isBusy}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          ADD / EDIT PRODUCT MODAL
          ========================================================================= */}
      {showAddEditModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <form onSubmit={handleSubmit}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <FiShoppingBag className="text-primary" />
                    {isEditMode ? "Edit Marketplace Product" : "Add New Marketplace Product"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddEditModal(false)}
                    disabled={submitting}
                  />
                </div>

                <div className="modal-body p-4">
                  {formError && (
                    <div className="alert alert-danger py-2 small mb-3">{formError}</div>
                  )}

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Product Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Teak Wood Doors / Ceramic Floor Tiles"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Marketplace Category <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedCategoryId}
                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Price (INR) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 1500"
                        value={price}
                        onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">
                        Pricing Unit (e.g. sq ft, piece, set, bag)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="piece"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">City</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Mumbai / Bangalore"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-dark">State</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Maharashtra / Karnataka"
                        value={stateName}
                        onChange={(e) => setStateName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Product Description
                    </label>
                    <AIDescriptionAssist
                      type="local_bazaar"
                      values={{ categoryName: nameOf(categories, selectedCategoryId), title, price, city, stateName }}
                      value={description}
                      onChange={setDescription}
                      showCounter
                    >
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Specify dimensions, specifications, grade, manufacturer, and warranty..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </AIDescriptionAssist>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold small text-dark">
                      Product Photos {!isEditMode && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      accept="image/*"
                      onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                    />
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowAddEditModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={submitting}
                  >
                    {submitting ? "Uploading & Saving..." : isEditMode ? "Save Changes" : "Publish Product"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          PRODUCT DETAILS MODAL
          ========================================================================= */}
      {showDetailsModal && selectedProduct && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow rounded-3">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  {selectedProduct.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDetailsModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                {detailsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <p className="text-muted small mt-2">Loading details...</p>
                  </div>
                ) : (
                  <div>
                    {selectedProduct.photos?.length > 0 && (
                      <div className="d-flex gap-2 overflow-auto pb-2 mb-3">
                        {selectedProduct.photos.map((p, i) => (
                          <img
                            key={i}
                            src={p.url?.startsWith("http") ? p.url : `https://images.allhomedesire.com${p.url}`}
                            alt="Product"
                            className="rounded-3 object-fit-cover shadow-sm"
                            style={{ width: "220px", height: "150px" }}
                          />
                        ))}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="badge bg-primary bg-opacity-10 text-primary fs-6">
                        {selectedProduct.localBazaarCategory?.name || "Local Bazaar"}
                      </span>
                      <h4 className="fw-bold text-primary mb-0">
                        ₹{Number(selectedProduct.price || 0).toLocaleString("en-IN")}{" "}
                        <span className="text-muted small fs-6">
                          /{selectedProduct.unit || "piece"}
                        </span>
                      </h4>
                    </div>

                    <h6 className="fw-bold text-dark mb-1">Description</h6>
                    <p className="text-secondary small mb-3" style={{ whiteSpace: "pre-wrap" }}>
                      {selectedProduct.description || "No description provided."}
                    </p>

                    <div className="card bg-light border-0 p-3 rounded-3">
                      <div className="text-muted small">
                        <FiMapPin className="text-danger me-1" size={12} />
                        Location: {selectedProduct.city ? `${selectedProduct.city}, ${selectedProduct.state || ""}` : "Local Area"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
